/* eslint-disable complexity */
import { createContext, useContext, useState, ReactNode } from "react";
import { UseFormReturn } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import moment from "moment";
import { omitBy } from "lodash";
import { useFormModal } from "@imminently/react/context/modals";
import { useUserRoles } from "@/hooks/useUserRoles";
import { encounterService } from "@/services";
import { formatName } from "@/global";
import {
  Patient,
  PatientEncounter,
  InfectiousSymptom,
} from "@/resources/types";
import { medicareSchemaProps } from "./medicareValidate";
import { createEmailSchemaWithDuplicateCheck } from "./emailOrEmptyStringSchema";

const optional = (schema: yup.AnySchema) =>
  yup.lazy((v) => (v === "" ? yup.string() : schema));

/**
 * Recursively trims all string values in an object.
 * Ensures clean data before submission and prevents whitespace-only input.
 * Handles special objects (Date, moment) without recursing into them.
 */
export const cleanFormValues = (obj: any): any => {
  try {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === "string") {
      return obj.trim();
    }

    // Don't recurse into special object types - return as-is
    if (obj instanceof Date) {
      return obj;
    }

    // Check for moment objects (has _isAMomentObject or isValid function)
    if (obj._isAMomentObject || (typeof obj.isValid === "function" && typeof obj.format === "function")) {
      return obj;
    }

    // Don't recurse into File, Blob, or other browser objects
    if (typeof File !== "undefined" && obj instanceof File) {
      return obj;
    }
    if (typeof Blob !== "undefined" && obj instanceof Blob) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(cleanFormValues);
    }

    if (typeof obj === "object") {
      const cleaned: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          cleaned[key] = cleanFormValues(obj[key]);
        }
      }
      return cleaned;
    }

    return obj;
  } catch (error) {
    // If anything goes wrong, return the original value rather than crashing
    console.warn("cleanFormValues: error cleaning value, returning original", error);
    return obj;
  }
};

const baseSchema = {
  submissionType: yup.mixed(),
  dob: optional(yup.date()),
  sex: optional(yup.mixed()),
  gender: optional(yup.mixed()),
  pronouns: yup.string().trim(),
  infectiousSymptoms: yup
    .array()
    .of(yup.mixed().oneOf(Object.values(InfectiousSymptom)))
    .required(),
  problem: yup.string().trim().required(),
  ...medicareSchemaProps,
  umrn: yup.string().trim(),
};

const createSchema = yup.object().shape({
  ...baseSchema,
  firstName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("First name is required")
      : schem;
  }),
  lastName: yup.string().trim().ensure().when("submissionType", ([submissionType], schem) => {
    return submissionType !== "unknown"
      ? schem.required("Last name is required")
      : schem;
  }),
  phone: optional(yup.string().phone()),
  email: createEmailSchemaWithDuplicateCheck(),
  // Delegate fields - optional
  hasDelegatePresent: yup.boolean(),
  delegateFirstName: yup.string().trim().when("hasDelegatePresent", ([hasDelegatePresent], schem) => {
    return hasDelegatePresent
      ? schem.required("Delegate first name is required")
      : schem;
  }),
  delegateLastName: yup.string().trim().when("hasDelegatePresent", ([hasDelegatePresent], schem) => {
    return hasDelegatePresent
      ? schem.required("Delegate last name is required")
      : schem;
  }),
  delegatePhone: yup.string().when("hasDelegatePresent", ([hasDelegatePresent], schem) => {
    return hasDelegatePresent
      ? optional(schem.phone())
      : schem;
  }),
  delegateEmail: yup.string().trim().when(["hasDelegatePresent", "delegateCanAct"], ([hasDelegatePresent, delegateCanAct], schem) => {
    return hasDelegatePresent && delegateCanAct
      ? schem.email("Invalid email format")
      : schem;
  }),
  delegateRelationship: yup.string().trim(),
  delegateCanAct: yup.boolean(),
});

const createEditSchema = (currentPatientId?: string) => yup.object().shape({
  ...baseSchema,
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  phone: optional(yup.string().phone()),
  email: createEmailSchemaWithDuplicateCheck(currentPatientId),
});

const initialState = {
  submissionType: "",
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  dob: undefined,
  sex: "",
  gender: "",
  pronouns: "",
  medicareNo: "",
  medicareNoPos: "",
  umrn: "",
  infectiousSymptoms: [],
  tasks: [],
  problem: "",
  pain: "",
  frailty: "",
  atsScore: "",
  location: "",
  complaintCode: "",
  triageCategory: "",
  nursingAssessment: "",
  patientComplaint: "",
  typeOfVisit: "",
  modeOfArrival: "",
  ambulanceCaseNumber: "",
  allergies: [],
  // Delegate fields
  hasDelegatePresent: false,
  delegateFirstName: "",
  delegateLastName: "",
  delegatePhone: "",
  delegateEmail: "",
  delegateRelationship: "",
  delegateCanAct: false,
};

export const buildPatientPayload = (rawData: any, submissionType?: string): Partial<Patient> => {
  // Clean all string values to remove leading/trailing whitespace
  const data = cleanFormValues(rawData);

  const patient: Partial<Patient> = {
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone,
    email: data.email,
    sex: data.sex,
    gender: data.gender,
    dateOfBirth: !data.dob ? undefined : moment(data.dob).format("YYYY-MM-DD"),
    medicareNo: data.medicareNo,
    medicareNoPos: data.medicareNoPos,
    umrn: data.umrn,
    pronouns: data.pronouns,
    allergies: data.allergies?.map((a: { description: string; severity: string }) => ({
      description: a.description,
      severity: a.severity,
    })) || [],
    // Use noAllergies from form data, but ensure it's false if allergies exist
    // If allergies array has items → noAllergies must be false
    // If allergies array is empty → use the form field value (defaults to false if not touched)
    noAllergies: (data.allergies?.length ?? 0) > 0 ? false : (data.noAllergies ?? false),
    // marshall gpDetails, nextOfKin, previousNames etc. from the flat form data into the nested object structure...
    // GP Details - build nested object if any field is provided
    gpDetails: (data.gpClinicName || data.gpDoctorFirstName || data.gpDoctorLastName) ? {
      clinicName: data.gpClinicName,
      doctorFirstName: data.gpDoctorFirstName,
      doctorLastName: data.gpDoctorLastName,
    } : undefined,
    // Next of Kin - build nested object if any field is provided
    nextOfKin: (data.nextOfKinFirstName || data.nextOfKinLastName || data.nextOfKinRelationship) ? {
      firstName: data.nextOfKinFirstName,
      lastName: data.nextOfKinLastName,
      relationship: data.nextOfKinRelationship,
    } : undefined,
    // Previous Names - include if array has entries (already cleaned by cleanFormValues)
    previousNames: data.previousNames && data.previousNames.length > 0 ? data.previousNames : undefined,
  };

  // Strip masked names (only asterisks) to allow backend to keep existing names
  // This handles de-anonymization where user leaves names as "****"
  if (patient.firstName && /^\*+$/.test(patient.firstName)) {
    delete patient.firstName;
  }
  if (patient.lastName && /^\*+$/.test(patient.lastName)) {
    delete patient.lastName;
  }

  if (submissionType === "anonymous") {
    patient.anonymous = true;
    patient.unknown = false; // Explicitly clear unknown when setting anonymous
  } else if (submissionType === "unknown") {
    patient.unknown = true;
    patient.anonymous = false; // Explicitly clear anonymous when setting unknown
  } else {
    // Standard patient - clear both flags
    patient.anonymous = false;
    patient.unknown = false;
  }
  return omitBy(patient, value => value === "");
};

export const buildEncounterSpecificPayload = (rawData: any, options?: { shouldClearDelegates?: boolean }): Partial<Omit<PatientEncounter, "patient" | "id">> => {
  // Clean all string values to remove leading/trailing whitespace
  const data = cleanFormValues(rawData);

  const payload: any = {
    infectiousSymptoms: data.infectiousSymptoms?.length > 0 ? data.infectiousSymptoms : undefined,
    tasks: data.tasks || [], // Ensure tasks array is always included (even if empty)
    frailty: data.frailty ? (isNaN(Number(data.frailty)) ? undefined : Number(data.frailty)) : undefined,
    pain: data.pain ? (isNaN(Number(data.pain)) ? undefined : Number(data.pain)) : undefined,
    typeOfVisit: data.typeOfVisit,
    modeOfArrival: data.modeOfArrival,
    ambulanceCaseNumber: data.ambulanceCaseNumber,
    triageDirection: data.triageDirection,
    patientOrigin: data.patientOrigin,
    location: data.location,
    atsScore: data.atsScore ? (isNaN(Number(data.atsScore)) ? undefined : Number(data.atsScore)) : undefined,
    triageChangeReason: data.triageChangeReason || undefined, // Include triage change reason if provided
    problem: {
      patient: data.patientComplaint, // patientComplaint maps to problem.patient
      triage: data.problem, // nursingAssessment maps to problem.triage
      nursingAssessment: data.nursingAssessment // nursingAssessment maps to problem.triage
    },
    complaintCode: data.complaintCode, // Include complaintCode in payload
    // New fields for NMHS configuration
    stream: data.stream,
    fdvRelated: data.fdvRelated,
    representIn48h: data.representIn48h,
    injury: data.injury,
    cause: data.cause,
    intent: data.intent,
    relatedAlcoholDrugs: data.relatedAlcoholDrugs,
  };

  // Handle delegates: explicitly clear if requested, otherwise add if present
  if (options?.shouldClearDelegates) {
    // Explicitly set empty array to clear delegates
    payload.delegates = [];
  } else if (data.hasDelegatePresent) {
    const delegate: any = {
      firstName: data.delegateFirstName || undefined,
      lastName: data.delegateLastName || undefined,
      relationship: data.delegateRelationship,
      from: Date.now(),
      // Only include phone and email if they can act (canAct is true)
      // canAct defaults to false (accompanying person) unless explicitly set to true
      canAct: data.delegateCanAct === true
    };

    // Include delegate ID if it exists (for editing existing delegates)
    if (data.delegateId) {
      delegate.id = data.delegateId;
    }

    // Only include phone and email if they can act
    if (data.delegateCanAct === true) {
      if (data.delegatePhone) delegate.phone = data.delegatePhone;
      if (data.delegateEmail) delegate.email = data.delegateEmail;
    }

    payload.delegates = [delegate];
    payload.relationship = data.delegateRelationship;
  }

  // Remove empty strings and undefined, but preserve tasks array and delegates array when explicitly cleared
  // First, extract tasks and delegates to preserve them
  const tasks = payload.tasks;
  const delegates = payload.delegates;
  const cleanedPayload = omitBy(payload, (value, key) => {
    // Always preserve tasks
    if (key === "tasks") return false;
    // Preserve delegates array if it was explicitly set (even if empty, to clear delegates)
    if (key === "delegates" && delegates !== undefined) return false;
    // Remove empty strings and undefined
    if (value === "" || value === undefined) return true;
    // Remove empty arrays (except tasks and delegates which we handle above)
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  });
  // Always include tasks in the payload
  cleanedPayload.tasks = tasks || [];
  // Include delegates if it was explicitly set
  if (delegates !== undefined) {
    cleanedPayload.delegates = delegates;
  }
  return cleanedPayload;
};

interface AddPatientContextValue {
  currentMode: "create" | "edit";
  setCurrentMode: (mode: "create" | "edit") => void;
  methods: UseFormReturn<any>;
  notify: {
    success: (message: string) => void;
    error: (message: string) => void;
  };
  close: (result?: boolean) => void;
  loadEncounter: (encounter: PatientEncounter) => void;
  loadPatient: (patient: Patient) => void;
  handleSubmitForm: (data: any) => void;
  createAction: any;
  createQuery: any;
  updateAction: any;
  isUpdateLoading: boolean;
  encounterData?: PatientEncounter;
  disableSubmit: boolean;
  pendingPatientData: any | null;
  setPendingPatientData: (data: any) => void;
  isClinicianOrAdmin: boolean;
}

const AddPatientContext = createContext<AddPatientContextValue | undefined>(undefined);

interface AddPatientProviderProps {
  children: ReactNode;
  initialMode?: "create" | "edit";
  initialEncounter?: PatientEncounter;
}

// eslint-disable-next-line complexity
const patientToFormValues = (patient: Patient) => {
  // Determine submission type based on patient flags
  // For anonymous patients, detect by masked names since backend deletes anonymous flag
  let submissionType = "";
  if (patient.firstName === "****" && patient.lastName === "****") {
    submissionType = "anonymous";
  } else if (patient.unknown) {
    submissionType = "unknown";
  }

  return {
    submissionType,
    firstName: patient.firstName || "",
    lastName: patient.lastName || "",
    phone: patient.phone || "",
    email: patient.email || "",
    dob: patient.dateOfBirth ? moment(patient.dateOfBirth, "YYYY-MM-DD") : undefined,
    sex: patient.sex || "",
    gender: patient.gender || "",
    pronouns: patient.pronouns || "",
    medicareNo: patient.medicareNo || "",
    medicareNoPos: patient.medicareNoPos || "",
    umrn: patient.umrn || "",
    allergies: patient.allergies || [],
  };
};

const ensureString = (value: any): string => {
  if (typeof value === "string") return value ?? "";
  if (typeof value === "number") return String(value);
  return "";
};

const encounterToFormValues = (encounter: PatientEncounter) => {
  const patient = encounter.patient || {};
  const encounterAny = encounter as any; // Type assertion for new backend fields

  return {
    ...patientToFormValues(patient),
    infectiousSymptoms: encounter.infectiousSymptoms || [],
    tasks: encounter.tasks || [],
    expects: encounter.problem?.expects || "",
    patientComplaint: encounter.problem?.patient || "", // patientComplaint maps from problem.patient
    problem: encounter.problem?.triage || "", // presentingComplaint maps from problem.triage
    complaintCode: encounterAny.complaintCode || "", // Extract complaintCode from encounter
    nursingAssessment: encounter.problem?.nursingAssessment || "", // nursingAssessment maps from problem.triage
    pain: ensureString(encounter.pain),
    frailty: ensureString(encounter.frailty),
    atsScore: ensureString(encounter.atsScore),
    location: encounter.location?.id || "",
    triageDirection: encounterAny.triageDirection || "", // Extract triageDirection from encounter
  };
};

type PatientEncounterFormValues = ReturnType<typeof encounterToFormValues>;

export const AddPatientProvider = ({ children, initialMode = "create", initialEncounter }: AddPatientProviderProps) => {
  const [currentMode, setCurrentMode] = useState<"create" | "edit">(initialMode);
  const [encounterData, setEncounterData] = useState<PatientEncounter | undefined>(initialEncounter);
  const [pendingPatientData, setPendingPatientData] = useState<any | null>(null);

  const { action: createAction, query: createQuery } = encounterService.useMutateResource(undefined, {});
  const { mutateAsync: updateAction, isLoading: isUpdateLoading } = encounterService.useUpdate();
  const { isClinicianOrAdmin } = useUserRoles();

  const { methods, notify, close: closeThisModal } = useFormModal<PatientEncounterFormValues>({
    mode: "onChange",
    defaultValues: encounterData ? encounterToFormValues(encounterData) : initialState,
    resolver: (yupResolver(currentMode === "edit" ? createEditSchema(encounterData?.patient?.id) : createSchema) as any),
  });

  const { reset, formState: { isValid }, handleSubmit } = methods;

  const loadEncounter = (encounterToLoad: PatientEncounter) => {
    setEncounterData(encounterToLoad);
    const formValues = encounterToFormValues(encounterToLoad);
    reset(formValues as any);
    setCurrentMode("edit");
  };

  const loadPatient = (patient: Patient) => {
    const currentValues = methods.getValues();
    const formValues = patientToFormValues(patient);
    reset({
      ...currentValues,
      ...formValues,
    } as any);

    // Create a virtual encounter with the selected patient for edit mode
    const virtualEncounter: PatientEncounter = {
      id: "virtual", // Placeholder ID
      patient: patient,
      status: "draft",
      created: new Date().toISOString(),
    } as PatientEncounter;

    setEncounterData(virtualEncounter);
    setCurrentMode("edit");
  };

  const handleSubmitForm = (data: any) => {
    const submissionType = data.submissionType;

    if (currentMode === "create") {
      const patientPayload = buildPatientPayload(data, submissionType);
      const encounterPayloadFromHelper = buildEncounterSpecificPayload(data);

      // Determine source based on delegate presence
      const source = data.hasDelegatePresent && (data.delegateFirstName || data.delegateLastName)
        ? "delegate"
        : "nurse";

      const encounterToCreate: Partial<PatientEncounter> = {
        ...encounterPayloadFromHelper,
        source,
        patient: patientPayload as Patient,
      };

      console.log("[Add patient - Create]", encounterToCreate);
      createAction(omitBy(encounterToCreate, (value) => value === "" && typeof value !== "boolean"))
        .then(() => {
          if (submissionType === "anonymous") {
            notify.success("Anonymous patient created");
          } else {
            const name = formatName(data.firstName, data.lastName);
            notify.success(`Created patient: ${name}`);
          }
          closeThisModal(true);
        })
        .catch(() => notify.error("Failed to create patient"));
    } else if (currentMode === "edit" && encounterData) {
      // Handle virtual encounter (creating new encounter with existing patient)
      if (encounterData.id === "virtual") {
        const patientPayload = buildPatientPayload(data, submissionType);
        const encounterPayloadFromHelper = buildEncounterSpecificPayload(data);

        // Determine source based on delegate presence
        const source = data.hasDelegatePresent && (data.delegateFirstName || data.delegateLastName)
          ? "delegate"
          : "nurse";

        const encounterToCreate: Partial<PatientEncounter> = {
          ...encounterPayloadFromHelper,
          source,
          patient: patientPayload as Patient,
        };

        console.log("[Add patient - Create with existing patient]", encounterToCreate);
        createAction(omitBy(encounterToCreate, (value) => value === "" && typeof value !== "boolean"))
          .then(() => {
            if (submissionType === "anonymous") {
              notify.success("Anonymous patient created");
            } else {
              const name = formatName(data.firstName, data.lastName);
              notify.success(`Created patient: ${name}`);
            }
            closeThisModal(true);
          })
          .catch(() => notify.error("Failed to create patient"));
        return;
      }

      // Detect anonymous patients by masked names since backend deletes anonymous flag
      const wasAnonymous = encounterData.patient?.firstName === "****" && encounterData.patient?.lastName === "****";
      const wasUnknown = !!encounterData.patient?.unknown;
      const isKnown = !wasAnonymous && !wasUnknown;

      // Guard illegal transitions in edit mode
      // Known -> Unknown/Anonymous is not allowed
      if (isKnown && (submissionType === "unknown" || submissionType === "anonymous")) {
        notify.error("Cannot change a known patient to unknown or anonymous");
        return;
      }
      // Anonymous -> Unknown is not allowed; also cannot change anonymity in edit
      if (wasAnonymous && submissionType !== "anonymous") {
        notify.error("Cannot change an anonymous patient's status");
        return;
      }
      // Unknown -> Known requires clinician/admin and names provided
      if (wasUnknown) {
        const switchingToStandard = submissionType === "";
        if (switchingToStandard) {
          if (!isClinicianOrAdmin) {
            notify.error("Only clinicians or admins can make an unknown patient known");
            return;
          }
          // Names must be present (schema also enforces, but double-check for clarity)
          if (!data.firstName || !data.lastName) {
            notify.error("First name and last name are required to make the patient known");
            return;
          }
        }
      }
      const patientUpdatePayload = buildPatientPayload(data);
      const encounterUpdatePayload = buildEncounterSpecificPayload(data);

      const payload: Partial<PatientEncounter> = {
        ...encounterUpdatePayload,
        patient: patientUpdatePayload as any,
      };

      console.log("[Add patient - Edit] Submitting payload:", payload);
      updateAction({ id: encounterData.id, payload })
        .then(() => {
          const name = formatName(data.firstName, data.lastName);
          notify.success(`Updated patient: ${name}`);
          closeThisModal(true);
        })
        .catch((err) => {
          console.error("Failed to update patient:", err);
          notify.error(`Failed to update patient${err?.message ? ": " + err.message : ""}`);
        });
    }
  };

  const disableSubmit =
    (currentMode === "create" && !isValid) ||
    (currentMode === "create" && createQuery.isLoading) ||
    (currentMode === "edit" && isUpdateLoading);

  const value: AddPatientContextValue = {
    currentMode,
    setCurrentMode,
    methods,
    notify,
    close: closeThisModal,
    loadEncounter,
    loadPatient,
    handleSubmitForm: handleSubmit(handleSubmitForm),
    createAction,
    createQuery,
    updateAction,
    isUpdateLoading,
    encounterData,
    disableSubmit,
    pendingPatientData,
    setPendingPatientData,
    isClinicianOrAdmin,
  };

  return (
    <AddPatientContext.Provider value={value}>
      {children}
    </AddPatientContext.Provider>
  );
};

export const useAddPatientContext = () => {
  const context = useContext(AddPatientContext);
  if (!context) {
    throw new Error("useAddPatientContext must be used within an AddPatientProvider");
  }
  return context;
};
